import React from 'react';
import { CircleCheck, Flag, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from 'components/ui/button';
import { StatusBadge } from 'components/ui/status-badge';
import { cn } from 'lib/utils';
import { ExamBar } from './ExamBar';
import { ExamClockChip, type ExamClock } from './ExamTimer';

export interface ExamTopBarProps {
    title: string;
    /** 1-based index of the current question. */
    questionNumber: number;
    totalQuestions: number;
    sectionTitle: string | null;
    isPreview: boolean;
    /** Show the autosave status (real attempt, not preview). */
    showSaveStatus: boolean;
    isSaving: boolean;
    /** `null` when the test is untimed or the start page hides the timer. */
    clock: ExamClock | null;
    showProgressBar: boolean;
    answeredCount: number;
    progressPct: number;
    isSubmitting: boolean;
    onSubmit: () => void;
    className?: string;
}

/** Autosave state: muted text, a green check once the last answer is stored. */
export function ExamSaveStatus({ isSaving }: { isSaving: boolean }) {
    const { t } = useTranslation();
    return (
        <span role="status" className="inline-flex shrink-0 items-center gap-1.5 text-[13px] text-muted-foreground">
            {isSaving ? (
                <Loader2 className="h-[15px] w-[15px] animate-spin" aria-hidden />
            ) : (
                <CircleCheck className="h-[15px] w-[15px] text-success-600" aria-hidden />
            )}
            {isSaving ? t('saving') : t('saved_just_now')}
        </span>
    );
}

/** Desktop exam header card. Props-only: all state lives in `TestTaking`. */
export function ExamTopBar({
    title,
    questionNumber,
    totalQuestions,
    sectionTitle,
    isPreview,
    showSaveStatus,
    isSaving,
    clock,
    showProgressBar,
    answeredCount,
    progressPct,
    isSubmitting,
    onSubmit,
    className,
}: ExamTopBarProps) {
    const { t } = useTranslation();
    return (
        <header className={cn('rounded-lg border border-border bg-card px-5 py-4', className)}>
            <div className="flex items-center gap-4">
                <div className="flex min-w-0 flex-1 flex-col gap-[3px]">
                    <h1 className="truncate text-base font-bold leading-tight tracking-[-0.01em]">{title}</h1>
                    <p className="truncate text-[13px] leading-tight text-muted-foreground">
                        {t('question')}{' '}
                        <span className="tabular-nums">
                            {questionNumber} {t('of')} {totalQuestions}
                        </span>
                        {sectionTitle && ` · ${sectionTitle}`}
                    </p>
                </div>
                {isPreview && <StatusBadge tone="warning">{t('preview')}</StatusBadge>}
                {showSaveStatus && <ExamSaveStatus isSaving={isSaving} />}
                {clock && <ExamClockChip clock={clock} />}
                <Button variant="ink" size="default" className="shrink-0 gap-1.5 px-[18px]" onClick={onSubmit} disabled={isSubmitting}>
                    <Flag className="h-4 w-4" aria-hidden />
                    {isSubmitting ? t('submitting') : t('review_and_submit')}
                </Button>
            </div>
            {showProgressBar && (
                <div className="mt-3.5 flex items-center gap-3.5 text-[12.5px] leading-tight text-muted-foreground">
                    <span>{t('progress')}</span>
                    <ExamBar pct={progressPct} className="h-1.5 flex-1" />
                    <span>
                        <span className="font-semibold tabular-nums text-foreground">
                            {answeredCount}/{totalQuestions}
                        </span>{' '}
                        {t('answered')}
                    </span>
                </div>
            )}
        </header>
    );
}

export default ExamTopBar;
